import { Configuration, LogLevel } from '@azure/msal-browser';

import type { ConsoleConfig } from './config/consoleConfig';

// FD-420 — MSAL configuration is BUILT from the runtime console config, not read
// from `import.meta.env` at module load.
//
// Before FD-420 this module read `VITE_CLIENT_ID` / `VITE_TENANT_ID` /
// `VITE_API_SCOPE` at import time and threw on a missing value — a throw that
// fired before `main.tsx` could render anything, so the operator got a white
// page and the reason went to the console. Those same three diagnostics now live
// in `consoleConfig.ts`'s DEV fallback and reach `BootFailure` instead.
//
// `API_SCOPE`, `loginRequest` and `apiTokenRequest` stay exported under their
// existing names as ESM LIVE BINDINGS: `buildMsalConfig` assigns them, and
// `App.tsx` and `api/client.ts` read them at call time — which is always after
// boot. That is what let this change land without touching a single consumer.

/** The AdminAccess scope URI (ADR-014). Bound by `buildMsalConfig`. */
export let API_SCOPE!: string;

/** Interactive sign-in request. Bound by `buildMsalConfig`. */
export let loginRequest!: { scopes: string[] };

/** Silent token request for Manager calls. Bound by `buildMsalConfig`. */
export let apiTokenRequest!: { scopes: string[] };

/**
 * Build the MSAL `Configuration` for this console, and bind the scope-derived
 * request objects above.
 *
 * It does both deliberately. Splitting the binding into a second exported call
 * would create exactly one new defect — forgetting it — whose symptom is a
 * token request with `scopes: undefined` at the first API call, long after boot
 * looked successful. Both outputs come from the same object; they are produced
 * together.
 *
 * Call ONCE, in `main.tsx`, before `MsalProvider` mounts.
 */
export function buildMsalConfig(cfg: ConsoleConfig): Configuration {
  API_SCOPE = cfg.scope;
  loginRequest = { scopes: ['User.Read', API_SCOPE] };
  apiTokenRequest = { scopes: [API_SCOPE] };

  // Normally absent from the config: the console redirects back to the host that
  // served it, so the origin is both correct and one fewer value to keep in step
  // with the Entra app registration's redirect-URI allowlist.
  const redirectUri =
    cfg.redirectUri ?? (typeof window !== 'undefined' ? `${window.location.origin}/` : undefined);

  return {
    auth: {
      clientId: cfg.clientId,
      // VERBATIM (ADR-014 parameterisation 2). A tenant GUID in Mode 1,
      // `https://login.microsoftonline.com/organizations` in Mode 2 — which is
      // why this is never interpolated from a tenant id here.
      authority: cfg.authority,
      redirectUri,
      postLogoutRedirectUri: redirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // Unchanged, and load-bearing under FD-ADR-040: one instance serves every
      // environment in a fleet, so the cache is shared across Manager hosts by
      // design. Never localStorage.
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (level === LogLevel.Error) {
            console.error(message);
          }
        },
        logLevel: LogLevel.Error,
      },
    },
  };
}
